import React from "react";
import { authorizedFetch } from "./boot.js";
import { LotIdentity } from "./lot_identity.jsx";
import { ReportDocument, ReportView } from "./report_document.jsx";
import { Card, Tag } from "./ui.jsx";
import { sydneyWhen } from "./time.js";

// Owner reports (vendor.reports.*@3, 23 September 2026): choose an owner, and the
// report covers every horse allocated to them; view it in full, or preview its
// email and send. Nothing is sent until the sender has reviewed a fresh server
// preview and presses the separate send button (VD-SPEC-LAUNCH-051/052).
// By horse chooses one horse or several, owned or not, for the full report only:
// email goes by owner, so a report never reaches someone about another owner's
// horse (owner's instruction, 24 September 2026).

async function request(path, options = {}) {
  const response = await authorizedFetch(path, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

function postCollection(path, input) {
  return request(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input }),
  });
}

function postAction(path, input, key) {
  return request(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: key }),
  });
}

const query = values => new URLSearchParams(values).toString();
const stateOf = receipt => String(receipt?.state || receipt?.status || "pending").toLowerCase();
const time = value => (value ? sydneyWhen(value) : "Not recorded");
const lotsLabel = refs => refs.map(lot => `Lot ${lot.number}`).join(", ");
// document@1's own bound (report_contracts.MAX_REPORT_LOTS); past it the host
// refuses the input, so the page says so before asking.
const MAX_REPORT_HORSES = 100;

export function Reports({ sale = "26R2R", vendor = window.VD.access.realm, initialLots = [], go }) {
  const [data, setData] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [historyError, setHistoryError] = React.useState("");
  const [historyRefreshing, setHistoryRefreshing] = React.useState(false);
  const [mode, setMode] = React.useState("owner");
  const [ownerId, setOwnerId] = React.useState(null);
  const [chosenLots, setChosenLots] = React.useState([]);
  const [includeRepository, setIncludeRepository] = React.useState(true);
  const [includeInspections, setIncludeInspections] = React.useState(true);
  const [includeNotes, setIncludeNotes] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [report, setReport] = React.useState(null);
  const [reportInput, setReportInput] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [opening, setOpening] = React.useState(false);
  const [previewing, setPreviewing] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [retry, setRetry] = React.useState(null);
  const mounted = React.useRef(true);
  const editVersion = React.useRef(0);
  const composePath = `/collections/vendor.reports.compose@3/?${query({ sale, vendor })}`;
  const historyPath = `/collections/vendor.reports.history@3/?${query({ sale, vendor })}`;

  const loadHistory = React.useCallback(async () => {
    setHistoryRefreshing(true);
    try {
      const value = await request(historyPath);
      if (mounted.current) { setHistory(value.items || []); setHistoryError(""); }
    } catch (_) {
      if (mounted.current) setHistoryError("Sent report history could not be loaded.");
    } finally { if (mounted.current) setHistoryRefreshing(false); }
  }, [historyPath]);

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    Promise.allSettled([request(composePath), request(historyPath)]).then(([compose, sent]) => {
      if (!active) return;
      if (compose.status === "fulfilled") {
        setData(compose.value);
        if (compose.value.sources?.repository === "unavailable") setIncludeRepository(false);
        // Arriving from a horse elsewhere in the dashboard chooses its owner,
        // when exactly one owner who can receive reports holds that horse.
        const holders = (compose.value.owners || []).filter(owner => !owner.optedOut
          && owner.lots.some(lot => initialLots.includes(lot.id)));
        // Otherwise the horses themselves are chosen.
        const arrived = (compose.value.lots || []).filter(lot => initialLots.includes(lot.id));
        if (holders.length === 1) setOwnerId(holders[0].id);
        else if (arrived.length) { setMode("horses"); setChosenLots(arrived.map(lot => lot.id)); }
      } else {
        setError("Reports could not be loaded. Refresh to try again.");
      }
      if (sent.status === "fulfilled") setHistory(sent.value.items || []);
      else setHistoryError("Sent report history could not be loaded.");
      setLoading(false);
    });
    return () => { active = false; mounted.current = false; };
    // initialLots is read once, on arrival, not tracked.
  }, [composePath, historyPath]);

  const owners = data?.owners || [];
  const owner = owners.find(row => row.id === ownerId) || null;
  const allLots = data?.lots || [];
  const ownerLots = owner ? allLots.filter(lot => owner.lots.some(ref => ref.id === lot.id)) : [];
  const chosen = allLots.filter(lot => chosenLots.includes(lot.id));
  const tooMany = chosen.length > MAX_REPORT_HORSES;
  const repositoryAvailable = Boolean(data?.sources?.repository) && data.sources.repository !== "unavailable";
  const composeLocked = sending || !!retry;
  const hasContent = (includeRepository && repositoryAvailable) || includeInspections || Boolean(note.trim());
  const canView = data?.canViewDocument === true && hasContent && !opening;
  const canPreview = mode === "owner" && data?.canPreview === true && data?.canSend === true
    && !!owner && !owner.optedOut
    && hasContent && !previewing && !sending && !retry;

  function changed(update) {
    if (composeLocked) return;
    editVersion.current += 1;
    update();
    setPreview(null); setDraft(null); setRetry(null); setNotice(""); setError("");
  }

  function options() {
    return {
      sale,
      include_repository: includeRepository && repositoryAvailable,
      include_inspections: includeInspections,
      include_notes: includeInspections && includeNotes,
      note: note.trim() || null,
    };
  }

  // The PDF is asked for with exactly the input the open report was built from.
  const loadPdf = React.useMemo(() => (reportInput
    ? () => postCollection("/collections/vendor.reports.pdf@1/", reportInput) : null), [reportInput]);

  async function openReport(selection) {
    setOpening(true); setError("");
    const input = { ...options(), ...selection };
    try {
      const value = await postCollection("/collections/vendor.reports.document@1/", input);
      if (mounted.current) { setReport(value); setReportInput(input); }
    } catch (failure) {
      if (mounted.current) setError(failure.status === 404
        ? "That report is no longer available. Refresh and choose again."
        : "The report could not be prepared. Try again.");
    } finally { if (mounted.current) setOpening(false); }
  }

  async function makePreview(withDraft = false) {
    if (!canPreview && !withDraft) return;
    const version = editVersion.current;
    const input = {
      ...options(), contact_id: owner.id,
      draft: withDraft && draft ? { contact_id: owner.id, ...draft } : null,
    };
    setPreviewing(true); setError(""); setNotice(""); setRetry(null);
    try {
      const value = await postCollection("/collections/vendor.reports.preview@3/", input);
      if (mounted.current && version === editVersion.current) {
        setPreview(value);
        setDraft({ subject: value.recipient.subject, body: value.recipient.body });
        setNotice("Preview ready. Nothing has been sent.");
      }
    } catch (failure) {
      if (!mounted.current || version !== editVersion.current) return;
      setError(failure.status === 404
        ? "That owner or their horses are no longer available. Refresh and choose again."
        : "The preview could not be prepared. Check the owner and content, then try again.");
    } finally { if (mounted.current) setPreviewing(false); }
  }

  async function send(attempt = null) {
    const next = attempt || {
      input: {
        ...options(), contact_id: owner.id, draft: { contact_id: owner.id, ...draft },
        preview_digest: preview?.previewDigest,
      },
      key: window.crypto.randomUUID(),
    };
    if (!next.input.preview_digest || sending) return;
    setSending(true); setError(""); setNotice("Queuing this report…");
    try {
      const receipt = await postAction("/actions/vendor.reports.send@3/", next.input, next.key);
      if (!mounted.current) return;
      const state = stateOf(receipt);
      setRetry(null); setPreview(null); setDraft(null);
      setNotice(["accepted", "pending"].includes(state)
        ? "Report queued. Delivery outcome will appear in sent history."
        : "Report request completed. Check sent history for its delivery outcome.");
      await loadHistory();
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if ([400, 404, 409, 412].includes(failure.status)) {
        setPreview(null); setRetry(null);
        setError(failure.status === 412
          ? "Something in this report changed since the preview. Prepare a new preview before sending."
          : "This preview is no longer current. Review the report and prepare a new preview before sending.");
      } else {
        setRetry(next);
        setError("The send request could not be confirmed. Retry the same request to avoid creating another report.");
      }
    } finally { if (mounted.current) setSending(false); }
  }

  return <div className="reports fade-in">
    <div className="report-layout">
      <div className="report-compose">
        <Card title={mode === "owner" ? "Choose an owner" : "Choose horses"}
          subtitle={mode === "owner" ? "The report covers every horse allocated to them"
            : "One horse or several, to view, print or save as PDF"}>
          {loading && <p role="status">Loading reports…</p>}
          {error && <p role="alert" className="error-text">{error}</p>}
          <div className="seg report-mode" role="group" aria-label="Report by">
            {[["owner", "By owner"], ["horses", "By horse"]].map(([id, label]) => <button type="button" key={id}
              data-report-mode={id} aria-pressed={mode === id} className={mode === id ? "on" : ""}
              disabled={composeLocked} onClick={() => mode !== id && changed(() => setMode(id))}>{label}</button>)}
          </div>
          {mode === "owner" && <>
          {data && !owners.length && <p className="muted">No owners are linked to your horses for this sale yet. Add them under People → Lot contacts.</p>}
          <div className="report-owners" aria-label="Owners">
            {owners.map(row => <button type="button" key={row.id} className="report-owner" data-owner={row.id}
              aria-pressed={row.id === ownerId} data-selected={row.id === ownerId ? "1" : undefined}
              disabled={row.optedOut || composeLocked}
              onClick={() => changed(() => setOwnerId(row.id))}>
              <strong>{row.name}</strong><span>{row.email}</span>
              <small>{lotsLabel(row.lots)}</small>
              {row.optedOut && <Tag>Stopped</Tag>}
            </button>)}
          </div>
          {owner && <div className="report-lots" aria-label={`Horses for ${owner.name}`}>
            {ownerLots.map(lot => <div key={lot.id} className="report-lot">
              <LotIdentity lot={lot}/>
            </div>)}
          </div>}
          </>}
          {mode === "horses" && <>
          {data && !allLots.length && <p className="muted">You have no horses in this sale yet.</p>}
          {allLots.length > 0 && <div className="report-horse-tools">
            <button type="button" className="btn sm" disabled={composeLocked}
              onClick={() => changed(() => setChosenLots(allLots.map(lot => lot.id)))}>Select all</button>
            <button type="button" className="btn sm" disabled={composeLocked || !chosen.length}
              onClick={() => changed(() => setChosenLots([]))}>Clear</button>
          </div>}
          <div className="report-lots" role="group" aria-label="Horses">
            {allLots.map(lot => <label key={lot.id} className="report-lot report-horse"
              data-selected={chosenLots.includes(lot.id) ? "1" : undefined}>
              <input type="checkbox" data-horse={lot.id} checked={chosenLots.includes(lot.id)} disabled={composeLocked}
                onChange={event => {
                  const on = event.target.checked;
                  changed(() => setChosenLots(current => on
                    ? [...current.filter(id => id !== lot.id), lot.id] : current.filter(id => id !== lot.id)));
                }}/>
              <LotIdentity lot={lot}/>
            </label>)}
          </div>
          </>}
        </Card>

        <Card title="Choose content" subtitle="What the report includes">
          <div className="report-options">
            <label data-unavailable={!repositoryAvailable ? "1" : undefined}>
              <input id="report-include-viewing" type="checkbox" checked={includeRepository && repositoryAvailable}
                disabled={!repositoryAvailable || composeLocked}
                onChange={event => changed(() => setIncludeRepository(event.target.checked))}/>
              <span><strong>Repository viewing</strong>
                <small>Views to date for each horse and each vet. Views today are not available yet.</small>
                {!repositoryAvailable && <small className="report-source unavailable">Viewing figures are unavailable right now</small>}
              </span>
            </label>
            <label>
              <input id="report-include-inspections" type="checkbox" checked={includeInspections}
                disabled={composeLocked}
                onChange={event => changed(() => setIncludeInspections(event.target.checked))}/>
              <span><strong>Inspections</strong><small>Every inspection, with the buyer, agent and time.</small></span>
            </label>
            <label data-unavailable={!includeInspections ? "1" : undefined}>
              <input id="report-include-notes" type="checkbox" checked={includeInspections && includeNotes}
                disabled={!includeInspections || composeLocked}
                onChange={event => changed(() => setIncludeNotes(event.target.checked))}/>
              <span><strong>Include inspection notes</strong><small>Your notes on each inspection. Off unless you tick it.</small></span>
            </label>
            <div className="field report-note">
              <label htmlFor="report-note">{mode === "owner" ? "Note for the owner" : "Note"} <span className="muted">(optional)</span></label>
              <textarea id="report-note" maxLength={4000} value={note} disabled={composeLocked}
                aria-describedby="report-note-help"
                onChange={event => changed(() => setNote(event.target.value))}/>
              <small id="report-note-help">{mode === "owner" ? "Appears in the email and the report." : "Appears in the report."}</small>
            </div>
          </div>
          {data && data.canViewDocument !== true && <p className="caption">Owner reports are not part of your access.</p>}
          {data?.deliveryReady === false && <p className="error-text">Report delivery is not available in this environment.</p>}
          {!hasContent && <p className="caption">Choose at least one thing to include.</p>}
          {mode === "horses" && tooMany && <p className="caption">Choose up to {MAX_REPORT_HORSES} horses for one report.</p>}
          {mode === "horses" && <p className="caption">Horse reports are to view, print or save as PDF. To email a report, choose By owner.</p>}
          <div className="report-primary-action">
            <span className="muted">{mode === "owner"
              ? (owner ? `${owner.name} · ${lotsLabel(owner.lots)}` : "Choose an owner")
              : (chosen.length ? `${chosen.length} ${chosen.length === 1 ? "horse" : "horses"} chosen` : "Choose horses")}</span>
            {/* By horse has no email, so viewing is its one and primary action. */}
            <button type="button" className={mode === "horses" ? "btn primary lg" : "btn lg"} data-view-report
              disabled={!canView || (mode === "owner" ? !owner : !chosen.length || tooMany)}
              onClick={() => openReport(mode === "owner" ? { contact_id: owner.id } : { lot_ids: chosen.map(lot => lot.id) })}>
              {opening ? "Preparing report…" : "View report"}</button>
            {mode === "owner" && <button type="button" className="btn primary lg" data-preview-report disabled={!canPreview}
              onClick={() => makePreview(false)}>{previewing ? "Preparing preview…" : "Preview email"}</button>}
          </div>
        </Card>

        {preview && draft && <ReportPreview preview={preview} draft={draft} deliveryReady={data?.deliveryReady === true}
          sending={sending} locked={composeLocked} previewing={previewing} onReview={() => makePreview(true)}
          onEdit={(field, value) => {
            editVersion.current += 1;
            setDraft(current => ({ ...current, [field]: value }));
            setPreview(current => ({ ...current, previewDigest: null }));
            setNotice("Email edited. Update the preview before sending.");
          }} onSend={() => send()}/>}
        {retry && <Card title="Send outcome unknown" className="report-retry">
          <p>The report is locked while its delivery state is unknown. Retry this exact request so another report is not created.</p>
          <button type="button" className="btn primary" disabled={sending} onClick={() => send(retry)}>
            {sending ? "Retrying…" : "Retry same send"}
          </button>
        </Card>}
        {notice && <p className="report-notice" role="status" aria-live="polite">{notice}</p>}
      </div>

      <aside className="report-side">
      {/* Email preferences used to be editable here as well as on Profile.
          Two live editors of one record is a way to be told two things, so
          this is now a pointer and Profile is the single place they are set. */}
      <Card title="Email preferences" subtitle="Now set once, on your Profile">
        <p className="muted">Which automated emails you receive is part of your account, not of this report.</p>
        {go
          ? <button type="button" className="btn" onClick={() => go("account")}>Open Profile</button>
          : <a className="btn" href="?tab=account">Open Profile</a>}
      </Card>
      <Card title="Sent history" subtitle="Delivery state recorded for this vendor" className="report-history"
        right={<button type="button" className="btn sm" disabled={historyRefreshing}
          onClick={loadHistory}>{historyRefreshing ? "Refreshing…" : "Refresh"}</button>}>
        {historyError && <p className="error-text">{historyError}</p>}
        {!historyError && !history.length && <p className="muted">No report has been sent yet.</p>}
        {history.map(item => <article key={item.id} className="report-history-row"
          data-outcome={String(item.outcome).toLowerCase()}>
          <div className="report-history-head"><strong>{item.recipient.name}</strong><Outcome value={item.outcome}/></div>
          <div>{item.email}</div>
          {item.subject && <div>{item.subject}</div>}
          <div className="muted">{lotsLabel(item.lots)}</div>
          <div className="caption">{item.sentAt ? `Sent ${time(item.sentAt)}` : `Queued ${time(item.queuedAt)}`} · {item.sender.name}</div>
        </article>)}
      </Card>
      </aside>
    </div>
    {report && <ReportView report={report} loadPdf={loadPdf}
      onClose={() => { setReport(null); setReportInput(null); }}/>}
  </div>;
}

function Outcome({ value }) {
  const label = ({ delivered: "Delivered", accepted: "Accepted", pending: "Pending", queued: "Queued",
    retryable: "Retrying", unknown: "Outcome unknown", failed: "Failed", bounced: "Bounced",
    complained: "Complaint", complaint: "Complaint", sent: "Sent", suppressed: "Stopped" })[String(value).toLowerCase()]
    || value || "Pending";
  return <Tag variant={String(value).toLowerCase() === "delivered" ? "live" : ""}>{label}</Tag>;
}

// The email as the owner will see it, as tall as its content, so nothing in it is
// clipped behind a fixed height (seen on dev, 25 Sep 2026). The sandbox allows
// same-origin, so the page can measure the email, but never scripts: nothing in
// the email can run, and the host builds and escapes all of it anyway.
function EmailFrame({ title, html }) {
  const frame = React.useRef(null);
  const fit = () => {
    const body = frame.current?.contentDocument?.documentElement;
    if (body) frame.current.style.height = `${body.scrollHeight + 2}px`;
  };
  return <iframe ref={frame} title={title} sandbox="allow-same-origin" srcDoc={html}
    className="report-email-frame" onLoad={fit}/>;
}

function ReportPreview({ preview, draft, deliveryReady, sending, locked, previewing, onReview, onEdit, onSend }) {
  const recipient = preview.recipient;
  return <Card title="Review the email" subtitle={`${recipient.name} · ${lotsLabel(recipient.lots)}`} className="report-preview">
    <p>The email carries the summary, with the full report attached as a PDF. Rewrite the subject and message as needed; the figures in the report do not change.</p>
    <section className="report-email-draft">
      <h4>{recipient.name}</h4><p className="muted">{recipient.email}</p>
      <label className="field">Subject
        <input aria-label={`Subject for ${recipient.name}`} value={draft.subject} maxLength={200} disabled={locked || previewing}
          onChange={event => onEdit("subject", event.target.value)}/>
      </label>
      <label className="field">Message
        <textarea aria-label={`Message for ${recipient.name}`} value={draft.body} maxLength={20000} rows={14} disabled={locked || previewing}
          onChange={event => onEdit("body", event.target.value)}/>
      </label>
      {preview.previewDigest
        ? <EmailFrame title={`Email preview for ${recipient.name}`} html={recipient.html}/>
        : <p role="status">Update the preview to see your edited email.</p>}
    </section>
    {/* What goes with the email: the preview's own frozen document, the one the
        attached PDF is rendered from, so the sender reviews exactly what is sent. */}
    <section className="report-attached" aria-label="The attached report">
      <h4>Attached as a PDF</h4>
      <p className="caption">{recipient.name} receives this full report with the email.</p>
      <div className="report-attached-body"><ReportDocument report={preview.document}/></div>
    </section>
    {!deliveryReady && <p className="error-text">Report delivery is not available in this environment.</p>}
    <div className="report-send-row">
      <p>Nothing is sent until you press Send report.</p>
      {!preview.previewDigest && <button type="button" className="btn" disabled={locked || previewing || !draft.subject.trim() || !draft.body.trim()}
        onClick={onReview}>{previewing ? "Preparing preview…" : "Update preview"}</button>}
      <button type="button" className="btn primary lg" data-send-report disabled={locked || previewing || !deliveryReady || !preview.previewDigest} onClick={onSend}>
        {sending ? "Sending…" : locked ? "Send status unknown" : "Send report"}
      </button>
    </div>
  </Card>;
}
